import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { DrawingService, LayerStylesService, MapService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { DebounceUtility } from '@ebw-monorepo/utils';
import { Collection, Feature, MapBrowserEvent } from 'ol';
import { Condition } from 'ol/events/condition';
import Circle from 'ol/geom/Circle';
import Point from 'ol/geom/Point';
import { Draw, Modify, Select } from 'ol/interaction';
import { DrawEvent } from 'ol/interaction/Draw';
import { ModifyEvent } from 'ol/interaction/Modify';
import VectorLayer from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { Fill, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { PsaService } from '../../../../services/psa.service';
import { Entity } from '../psa-configurator.model';

/**
 * @Author Tommaso Lazzaretti
 *
 * Component responsible for managing the duplication of PSA (Piano Sicurezza Acque) entities.
 * This component allows users to draw, modify, and select geometries (specifically circles) on the map,
 * and duplicate PSA information to nearby selected entities.
 *
 * The component interacts with various services for handling map interactions, drawing tools, and PSA duplication logic.
 * It manages temporary vector layers for the features drawn and provides an interface for manual adjustments of geometries.
 *
 * @class PsaDuplicateComponent
 * @implements {OnInit, OnDestroy}
 */
@Component({
  selector: 'ebw-monorepo-psa-duplicate',
  templateUrl: './psa-duplicate.component.html',
  styleUrls: ['./psa-duplicate.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      state('void', style({ opacity: 0 })), // Stato iniziale (quando l'elemento non è presente)
      transition(':enter', [
        // Quando l'elemento entra
        style({ opacity: 0 }), // Partiamo da opacità 0
        animate('500ms ease-out', style({ opacity: 1 })) // Animiamo a opacità 1
      ]),
      transition(':leave', [
        // Quando l'elemento esce
        animate('500ms ease-in', style({ opacity: 0 })) // Animiamo a opacità 0
      ])
    ])
  ],
  encapsulation: ViewEncapsulation.None
})
export class PsaDuplicateComponent implements OnInit, OnDestroy {
  DRAWING_INTERACTION_KEY = 'DRAWING_INTERACTION';
  MODIFY_INTERACTION_KEY = 'MODIFY_INTERACTION';
  SELECT_INTERACTION_KEY = 'SELECT_INTERACTION';
  MAX_SELECTED_ITEMS_NUMBER = 30;

  psaObject: Entity = null;
  listEntities: Entity[] = null;

  private _debounceUtility = new DebounceUtility(300); // 300 è il tempo di debounce in millisecondi

  filterTerm: string = null;
  selectedItems: Entity[] = [];

  /** Default EPSG for the drawing tool */
  DEFAULT_EPSG = 'EPSG:3857';
  /** Indicates the EPSG that is currently being used to display the coordinates of the drawn features. */
  currentEPSG = this.DEFAULT_EPSG;
  /** Layer where the complete drawings are stored and displayed. It gets added when the component inits and gets removed when the component is destroyed. */
  temporalDrawingLayer: VectorLayer<any>;
  /** Source for the temporalDrawingLayer. */
  temporalDrawingSource: VectorSource;
  /** Support variable for Circle polygon */
  isDrawn = false;
  /** Layer where the drawings and modifications are temporarily stored before being added to the temporalDrawingLayer. */
  temporalEditingLayer: VectorLayer<any>;
  /** Source for the temporalEditingLayer */
  temporalEditingSource: VectorSource;
  /**
   * Global Drawing interaction for drawing any type of features.
   * This interaction gets initialized and added when a feature for drawing is selected and removed when a drawing is completed.
   */
  drawingInteraction: Draw = null;
  /**
   * Global Drawing interaction for modifying any type of features.
   * This interaction gets initialized and added when a feature is drawn and enters the "Editing" phase.
   */
  modifyInteraction: Modify = null;
  /**
   * Options for the Circle drawing. Contains the coordinates of the center of the circle in the selected EPSG
   * and the radius of the circle in meters.
   * The center and the radius properties of this object are used to build the editing panel form fields.
   */
  circleOptions = {
    radius: 0,
    center: [0, 0]
  };
  /**
   * Global Drawing interaction for selecting features that exist in the temporal drawing layer.
   * It is active while the drawing tool is open and disabled during drawing and editing.
   */
  selectInteraction: Select = null;
  /**
   * General options for the figure editing. Contains the custom name of the geometry and the reference
   * system in which the coordinates are displayed.
   */
  generalOptions = {
    geometryName: '',
    EPSG: this.DEFAULT_EPSG
  };
  figureTranslation: { [id: string]: string } = {};

  constructor(
    private _psaService: PsaService,
    private _mapService: MapService,
    private _drawingService: DrawingService,
    private _styleService: LayerStylesService,
    private _ebwModalService: EbwModalService,
    private _translateService: I18NService
  ) {
    this.psaObject = this._psaService.psaFrom;

    this._debounceUtility.debouncedSubject.subscribe((value) => {
      this._psaService._getListElements(value, this.psaObject.tipologia).subscribe((value) => {
        this.listEntities = value;
        this.updateListEntitiesSelection();
      });
    });
  }

  ngOnInit(): void {
    this.figureTranslation['circle'] = this._translateService.getTranslateParameter('map.drawing_tool.circle');

    //Create temporal drawing layer when drawing tool is open
    this.temporalDrawingSource = new VectorSource({ wrapX: false });
    this.temporalDrawingLayer = new VectorLayer({
      source: this.temporalDrawingSource
    });
    this.temporalDrawingLayer.setZIndex(20);
    this._mapService.openLayersMap.addLayer(this.temporalDrawingLayer);

    //Create the select interaction for the drawn features.
    this.selectInteraction = new Select({
      layers: [this.temporalDrawingLayer],
      hitTolerance: 10,
      multi: true
    });
    this._mapService.addMapInteraction(this.SELECT_INTERACTION_KEY, this.selectInteraction);
  }

  /**
   * Function to update the center and radius of a Circle drawing or edit.
   * The modification comes from the form fields in the edit feature panel and is applied to the Circle feature currently being edited.
   */
  manualUpdateCircle() {
    const featureList = this.temporalEditingSource.getFeatures();
    //If there is a feature to update
    if (featureList.length > 0) {
      //Radius always in "meters", so conversion is needed.
      const radius = this._drawingService.convertMetersToRadius(this.circleOptions.radius, this._mapService.openLayersMap.getView());
      //Transform the coordinates from the EPSG that is specified in options to 3857
      const center = transform(this.circleOptions.center, this.generalOptions.EPSG, MapService.EPSG_3857_KEY);
      const feature = featureList[0] as Feature<Circle>;
      feature.getGeometry().setCenterAndRadius(center, radius);

      this.getNearlyEntities();
    }
  }

  /**
   * OpenLayers Condition for only allowing interactions to trigger on a left click.
   * @param event Click event
   * @returns True or false if the condition of the left click is fulfilled.
   */
  leftClickOnlyCondition: Condition = (event: MapBrowserEvent<any>) => {
    return event.originalEvent.button == 0;
  };

  /**
   * Set up drawing and configures the drawing parameters and functionalities depending on the
   * figure type, Circle in this case, that is a DrawingGeometries type.
   *
   * The temporalEditingLayer gets initialized and added to the map everytime a new drawing is added.
   */
  startDraw() {
    this.isDrawn = true;
    //Add the editing layer
    this.temporalEditingSource = new VectorSource({ wrapX: false });
    this.temporalEditingLayer = new VectorLayer({
      source: this.temporalEditingSource
    });

    this.drawingInteraction = new Draw({
      source: this.temporalEditingSource,
      type: 'Circle',
      condition: this.leftClickOnlyCondition
    });

    this.temporalEditingLayer.setZIndex(20);
    this._mapService.openLayersMap.addLayer(this.temporalEditingLayer);

    //Initialize the drawing interaction of a circle.
    this.drawingInteraction = new Draw({
      source: this.temporalEditingSource,
      type: 'Circle',
      condition: this.leftClickOnlyCondition
    });

    //After the draw is completed
    this.drawingInteraction.on('drawend', (drawnFeature: DrawEvent) => {
      const feature = drawnFeature['feature'] as Feature<Circle>;
      feature.setStyle([
        //Set an array of styles.
        //Main fill style for the circle
        this._styleService.buildStyleFromFeatureStyle(this._drawingService.orangeFillFeatureStyle()),
        //Style for painting the central point
        new Style({
          geometry: (feature: Feature<Circle>) => {
            return new Point(feature.getGeometry().getCenter());
          },
          image: new CircleStyle({
            radius: 4,
            fill: new Fill({
              color: 'rgba(0,0,0,0.0)'
            })
          })
        })
      ]);
      //Set the circleOptions properties.
      const center = feature.getGeometry().getCenter();
      this.circleOptions.center = transform(center, MapService.EPSG_3857_KEY, this.currentEPSG);
      this.circleOptions.radius = this._drawingService.convertRadiusToMeters(
        feature.getGeometry().getRadius(),
        this._mapService.openLayersMap.getView()
      );
      //Remove the drawing interaction because now the editing is done with the modify interaction.
      this._mapService.removeMapInteraction(this.DRAWING_INTERACTION_KEY);

      //Add the modify interaction only when the circle drawing has finished.
      if (!this.modifyInteraction) {
        this.modifyInteraction = new Modify({
          source: this.temporalEditingSource,
          style: this._styleService.buildStyleFromFeatureStyle(this._drawingService.blueFillFeatureStyle())
        });
        //When a modification is made
        this.modifyInteraction.on('modifyend', (modify: ModifyEvent) => {
          const featureCollection = modify.features as Collection<Feature>;
          const center = (featureCollection.item(0) as Feature<Circle>).getGeometry().getCenter();
          //Save the center to be displayed in the current EPSG
          this.circleOptions.center = transform(center, MapService.EPSG_3857_KEY, this.currentEPSG);
          this.circleOptions.radius = this._drawingService.convertRadiusToMeters(
            (featureCollection.item(0) as Feature<Circle>).getGeometry().getRadius(),
            this._mapService.openLayersMap.getView()
          );

          this.getNearlyEntities();
        });
        this._mapService.addMapInteraction(this.MODIFY_INTERACTION_KEY, this.modifyInteraction);
        this.getNearlyEntities();
      }
    });
    this._mapService.addMapInteraction(this.DRAWING_INTERACTION_KEY, this.drawingInteraction);
  }

  /**
   * End an ongoing draw and reset all the corresponding variables.
   * - Reset the isDrawing and isEditing variables.
   * - Remove the temporalEditingLayer and clears the source.
   * - Set the select interaction active again.
   * - Reset the option object of each of the geometries.
   * - Remove tooltips, drawing and modify interactions.
   */
  endDraw() {
    //Set the local drawing to initial value and list variables empty
    this.isDrawn = false;
    this.listEntities = [];

    //remove editing layer
    if (this.temporalEditingSource) {
      this.temporalEditingSource.clear();
    }
    this._mapService.openLayersMap.removeLayer(this.temporalEditingLayer);

    //add the select interaction
    this.selectInteraction.setActive(true);
    //Set the default values of generalOptions
    this.generalOptions.EPSG = this.DEFAULT_EPSG;
    this.generalOptions.geometryName = '';
    this.currentEPSG = this.DEFAULT_EPSG;
    //Reset circle options
    this.circleOptions.radius = 0;
    this.circleOptions.center = [0, 0];
    //Reset
    this._mapService.removeMapInteraction(this.DRAWING_INTERACTION_KEY);
    this._mapService.removeMapInteraction(this.MODIFY_INTERACTION_KEY);
    //Delete the drawing click event
    this.drawingInteraction = null;
    this.modifyInteraction = null;
  }

  /**
   * call _psaService Select for retrieve nearly entities
   * @private
   */
  private getNearlyEntities() {
    const center4326 = transform(this.circleOptions.center, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    this._psaService
      ._selectEntitiesRisksMatrix(center4326[0], center4326[1], this.circleOptions.radius, this.psaObject.tipologia)
      .subscribe((resp) => {
        this.listEntities = resp.result?.features?.map((feature) => feature.properties);
        this.updateListEntitiesSelection();
      });
  }

  /**
   * call _psaService duplicateRisksMatrix for duplicate psa
   * Duplicates PSA risk matrices from the selected entity to the nearby entities.
   * If an error occurs, an error modal is displayed.
   * @private
   */
  private duplicatePsa() {
    this._psaService
      ._duplicateRisksMatrix({
        id_from: this.psaObject.id,
        ids_to: this.selectedItems.map((entity) => {
          return entity.id;
        })
      })
      .subscribe({
        next: (r) => {
          this._ebwModalService.openErrorModal({
            title: 'Operazione eseguita con successo',
            content: 'Operazione eseguita con successo'
          });
          this.endDraw();
          this._psaService.toggleDuplicatePsa();
          this._mapService.clearCache();
        },
        error: (e) => {
          this._ebwModalService.openErrorModal({
            title: 'Errore',
            content: e.error.message
          });
        }
      });
  }

  /**
   * call _psaService duplicateChecklist for duplicate psa
   * Duplicates PSA checklists from the selected entity to the nearby entities.
   * If an error occurs, an error modal is displayed.
   * @private
   */
  private duplicateChecklist() {
    this._psaService
      ._duplicateChecklist({
        id_from: this.psaObject.id,
        ids_to: this.selectedItems.map((entity) => {
          return entity.id;
        })
      })
      .subscribe({
        next: (r) => {
          this._ebwModalService.openErrorModal({
            title: 'Operazione eseguita con successo',
            content: 'Operazione eseguita con successo'
          });
          this.endDraw();
          this._psaService.toggleDuplicatePsa();
          this._mapService.clearCache();
        },
        error: (e) => {
          this._ebwModalService.openErrorModal({
            title: 'Errore',
            content: e.error.message
          });
        }
      });
  }

  /**
   * Update the list of entities with the selected items.
   * @private
   */
  private updateListEntitiesSelection() {
    const psaArray = Array.isArray(this.psaObject) ? this.psaObject : [this.psaObject]; // Assicuriamoci che sia un array
    const psaIds = new Set(psaArray.map((obj: Entity) => Number(obj.id))); // Set di ID da eliminare completamente
    const selectedIds = new Set(this.selectedItems.map((item: Entity) => item.id)); // Set di ID da marcare come selected

    this.listEntities = this.listEntities
      .filter((entity) => !psaIds.has(Number(entity.id))) // Rimuoviamo quelli che sono in psaIdsupda
      .map((entity) => ({
        ...entity,
        selected: selectedIds.has(entity.id) // Se è in selectedItems, lo marchiamo
      }));
  }

  /**
   * Duplicate the selected PSA entity.
   */
  duplicateEntity() {
    if (this._psaService.typeDuplicatePsa === 'MATRICE_RISCHIO') {
      this.duplicatePsa();
    } else if (this._psaService.typeDuplicatePsa === 'CHECKLIST') {
      this.duplicateChecklist();
    } else {
      this._ebwModalService.openErrorModal({
        title: 'Errore Duplicazione',
        content: 'errore nella tipologia di duplicazione'
      });
    }
  }

  /**
   * Close the modal and reset the drawing tool.
   */
  closeModal() {
    this.endDraw();
    this._psaService.toggleDuplicatePsa();
  }

  /**
   * Toggle the selection of an entity.
   * @param entity
   */
  toggleSelection(entity: Entity) {
    if (this.selectedItems.length >= this.MAX_SELECTED_ITEMS_NUMBER) return;

    if (entity.selected) {
      this.selectedItems.push(entity);
    } else {
      this.selectedItems = this.selectedItems.filter((item) => item !== entity);
    }
  }

  /**
   * Check if an entity is selected.
   * @param entity
   */
  isSelected(entity: Entity): boolean {
    return this.selectedItems.some((item) => item.id === entity.id);
  }

  /**
   * Remove an element from the selected items.
   * @param element
   */
  remove(element: Entity) {
    this.selectedItems = this.selectedItems?.filter((item) => item.id !== element.id) || [];
    const foundItem = this.listEntities?.find((item) => item.id === element.id);
    if (foundItem) {
      foundItem.selected = false;
    }
  }

  /**
   * Function to handle the input change event and trigger the debounce utility.
   */
  onInputChange(): void {
    this._debounceUtility.trigger(this.filterTerm);
  }

  /**
   * Get the type of the PSA that is being duplicated.
   */
  get typeDuplicatePsa(): 'MATRICE_RISCHIO' | 'CHECKLIST' {
    return this._psaService.typeDuplicatePsa;
  }

  ngOnDestroy() {
    this._psaService.hideDuplicatePsa();
  }
}
